import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../services/api';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  currency?: string;
  firm_id?: number;
  is_finance_user?: boolean;
  firm?: {
    id: number;
    name: string;
    type: string;
    status: string;
  };
  firms?: Array<{
    id: number;
    name?: string;
    type?: string;
  }>;
  roles?: Array<{
    id: number;
    name: string;
    slug: string;
    scope: string;
    description?: string;
  }>;
  isPmpAdmin?: () => boolean;
  isFirmAdmin?: () => boolean;
}

interface MasqueradeData {
  is_masquerading: boolean;
  can_masquerade?: boolean;
  original_user?: {
    id: number;
    name: string;
    email: string;
  };
  masquerade_started_at?: string;
  duration?: string;
}

interface AuthContextType {
  user: User | null;
  masquerade: MasqueradeData | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: any) => Promise<void>;
  logout: () => void;
  updateUser: (userData: User) => void;
  refreshUserData: () => Promise<void>;
  socialLogin: (provider: string) => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [masquerade, setMasquerade] = useState<MasqueradeData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await api.get('/user');
      setUser(response.data.data.user);
      setMasquerade(response.data.data.masquerade);
    } catch (error) {
      localStorage.removeItem('token');
      delete api.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const refreshUserData = async () => {
    await fetchUser();
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await api.post('/login', { email, password });
      const { token, user } = response.data.data;
      
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      // Clear masquerade data on normal login
      setMasquerade(null);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  const register = async (userData: any) => {
    try {
      const response = await api.post('/register', userData);
      const { token, user } = response.data.data;
      
      localStorage.setItem('token', token);
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      // Clear masquerade data on registration
      setMasquerade(null);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setMasquerade(null);
  };

  const updateUser = (userData: User) => {
    setUser(userData);
  };

  const socialLogin = async (provider: string): Promise<string> => {
    const response = await api.get(`/login/${provider}`);
    return response.data.url;
  };

  const value = {
    user,
    masquerade,
    loading,
    login,
    register,
    logout,
    updateUser,
    refreshUserData,
    socialLogin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 